import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { addDays, startOfWeek } from 'date-fns'
import { api } from '../../services/api'
import { useAuthStore } from '../../context/auth'
import { Button, Card, Select, Spinner, Badge, EmptyState } from '../../components/ui'
import { Icon } from '../../components/ui/icons'
import {
  APPOINTMENT_CHANNEL_LABELS,
  APPOINTMENT_STATUS_COLORS,
  APPOINTMENT_STATUS_LABELS,
  formatDate,
  formatTime,
  weekdayName,
} from '../../utils/format'
import type { Appointment, Location, Professional } from '../../types/api'
import { NewAppointmentModal } from './NewAppointmentModal'
import { AppointmentDetailModal } from './AppointmentDetailModal'
import { TransitControls } from './TransitControls'

type ViewMode = 'day' | 'week'

export function AgendaPage() {
  const user = useAuthStore((s) => s.user)
  const [view, setView] = useState<ViewMode>('day')
  const [anchor, setAnchor] = useState(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  })
  const [professionalId, setProfessionalId] = useState('')
  const [locationId, setLocationId] = useState('')
  const [showNew, setShowNew] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const range = useMemo(() => {
    if (view === 'day') return { from: anchor, to: addDays(anchor, 1) }
    const from = startOfWeek(anchor, { weekStartsOn: 1 })
    return { from, to: addDays(from, 7) }
  }, [anchor, view])

  const professionals = useQuery({
    queryKey: ['professionals'],
    queryFn: async () => (await api.get<Professional[]>('/professionals')).data,
  })
  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: async () => (await api.get<Location[]>('/catalog/locations')).data,
  })
  const appointments = useQuery({
    queryKey: ['appointments', range.from.toISOString(), range.to.toISOString(), professionalId, locationId],
    queryFn: async () =>
      (
        await api.get<Appointment[]>('/appointments', {
          params: {
            from: range.from.toISOString(),
            to: range.to.toISOString(),
            ...(professionalId ? { professionalId } : {}),
            ...(locationId ? { locationId } : {}),
          },
        })
      ).data,
  })

  const canSchedule = user && ['ADMIN', 'RECEPTION'].includes(user.role)
  const days = view === 'day' ? [range.from] : Array.from({ length: 7 }, (_, i) => addDays(range.from, i))
  const appts = appointments.data ?? []

  function apptsOf(day: Date): Appointment[] {
    const next = addDays(day, 1)
    return appts.filter((a) => {
      const t = new Date(a.startsAt)
      return t >= day && t < next
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold text-ink">Agenda</h1>
        <div className="flex flex-wrap items-center gap-2">
          {/* Pantalla de llamados para el monitor de la sala de espera. */}
          <a
            href={`/llamados${locationId ? `?sede=${locationId}` : ''}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-steel-200 bg-white px-3 py-2 text-sm font-medium text-steel-700 transition-colors hover:border-brand-400 hover:bg-soft"
          >
            <Icon name="callPatient" className="h-4 w-4 text-brand-600" />
            Pantalla de sala de espera
          </a>
          {canSchedule && (
            <Button variant="cta" onClick={() => setShowNew(true)}>
              + Nueva cita
            </Button>
          )}
        </div>
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <Button variant="secondary" onClick={() => setAnchor(addDays(anchor, view === 'day' ? -1 : -7))}>←</Button>
            <Button variant="secondary" onClick={() => { const d = new Date(); d.setHours(0, 0, 0, 0); setAnchor(d) }}>
              Hoy
            </Button>
            <Button variant="secondary" onClick={() => setAnchor(addDays(anchor, view === 'day' ? 1 : 7))}>→</Button>
          </div>
          <p className="min-w-40 text-sm font-medium text-ink">{formatDate(anchor)}</p>
          <div className="flex rounded-lg border border-gray-300 p-0.5">
            {(['day', 'week'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setView(mode)}
                className={`rounded-md px-3 py-1 text-sm ${view === mode ? 'bg-brand-600 text-white' : 'text-gray-600'}`}
              >
                {mode === 'day' ? 'Día' : 'Semana'}
              </button>
            ))}
          </div>
          {user?.role !== 'DOCTOR' && (
            <Select value={professionalId} onChange={(e) => setProfessionalId(e.target.value)} className="max-w-56">
              <option value="">Todos los profesionales</option>
              {(professionals.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.firstName} {p.lastName}
                </option>
              ))}
            </Select>
          )}
          {(locations.data?.length ?? 0) > 1 && (
            <Select value={locationId} onChange={(e) => setLocationId(e.target.value)} className="max-w-44">
              <option value="">Todas las sedes</option>
              {(locations.data ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
          )}
        </div>
      </Card>

      {appointments.isLoading ? (
        <Spinner />
      ) : (
        <div className={view === 'week' ? 'grid gap-3 md:grid-cols-2 xl:grid-cols-7' : ''}>
          {days.map((day) => {
            const dayAppts = apptsOf(day)
            return (
              <Card key={day.toISOString()} title={weekdayName(day)} className="min-w-0">
                {dayAppts.length === 0 ? (
                  <EmptyState message="Sin citas" />
                ) : (
                  <ul className="space-y-1.5">
                    {dayAppts.map((a) => (
                      <li key={a.id} className="rounded-lg border border-gray-200 transition-colors hover:border-brand-500">
                        <button
                          onClick={() => setSelectedId(a.id)}
                          className="w-full rounded-t-lg px-2.5 py-2 text-left text-sm hover:bg-soft"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="flex items-center gap-1.5 font-semibold text-ink">
                              {formatTime(a.startsAt)}
                              {/* Reserva hecha por el paciente desde el sitio. */}
                              {a.channel === 'WEB' && (
                                <Badge className="bg-brand-100 text-brand-700">
                                  {APPOINTMENT_CHANNEL_LABELS.WEB}
                                </Badge>
                              )}
                              {/* Cupo de la segunda tanda de un día copado (DT-62):
                                  se solapa con la hora de al lado a propósito. */}
                              {a.isOverbooking && <Badge className="bg-amber-100 text-amber-800">Sobrecupo</Badge>}
                            </span>
                            <Badge className={APPOINTMENT_STATUS_COLORS[a.status]}>
                              {APPOINTMENT_STATUS_LABELS[a.status]}
                            </Badge>
                          </div>
                          <p className="truncate">
                            {a.patient.firstName} {a.patient.lastName}
                          </p>
                          <p className="truncate text-xs text-gray-500">
                            {a.service.name} · {a.professional.firstName} {a.professional.lastName}
                            {a.box ? ` · ${a.box.name}` : ''}
                          </p>
                        </button>
                        {/* Tránsito del día directo en la fila (vista día), como la
                            lista de citas por sesión del HIS de referencia. */}
                        {view === 'day' && (
                          <div className="border-t border-gray-100 px-2.5 py-2">
                            <TransitControls appointment={a} compact />
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            )
          })}
        </div>
      )}

      {showNew && <NewAppointmentModal onClose={() => setShowNew(false)} />}
      {selectedId && <AppointmentDetailModal appointmentId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  )
}
